import { useEffect, useState } from 'react';
import { Sheet } from '../ui/Sheet';
import { ReviewDetail } from './ReviewDetail';
import { ReviewContextPanel } from './ReviewContextPanel';
import type {
  ReviewDetail as ReviewDetailType,
  ReviewContextData,
} from './types';

type Tab = 'detail' | 'context';

interface ReviewDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  detail: ReviewDetailType | null;
  context: ReviewContextData | null | 'loading';
}

/**
 * Right-anchored panel that hosts the findings/round history (`detail` tab) and
 * the assembled-prompt breakdown (`context` tab) side-by-side instead of
 * stacking them below the review list. Keeps the list in the viewport so users
 * can compare multiple sessions without losing scroll position.
 */
export function ReviewDetailDrawer({ open, onClose, detail, context }: ReviewDetailDrawerProps) {
  const [tab, setTab] = useState<Tab>('detail');

  useEffect(() => {
    if (open) setTab('detail');
  }, [open, detail?.sessionId]);

  const title = detail
    ? `#${detail.prNumber} — ${detail.prTitle}`
    : 'Loading review…';

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <div className="sticky top-0 z-10 bg-gray-900 border-b border-gray-800 flex gap-1 px-4 pt-3">
        <TabButton active={tab === 'detail'} onClick={() => setTab('detail')} testId="drawer-tab-detail">
          Detail
        </TabButton>
        <TabButton active={tab === 'context'} onClick={() => setTab('context')} testId="drawer-tab-context">
          Context
        </TabButton>
      </div>
      <div className="p-4">
        {tab === 'detail' && (
          <div data-testid="drawer-panel-detail">
            {detail ? (
              <ReviewDetail detail={detail} />
            ) : (
              <div className="py-12 text-center text-gray-500 text-sm">Loading review detail…</div>
            )}
          </div>
        )}
        {tab === 'context' && (
          <div data-testid="drawer-panel-context">
            <ReviewContextPanel context={context} />
          </div>
        )}
      </div>
    </Sheet>
  );
}

function TabButton({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors border-b-2 ${
        active
          ? 'border-teal-500 text-white bg-gray-900'
          : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
      }`}
    >
      {children}
    </button>
  );
}
